import { useCallback, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Masonry from 'react-masonry-css';
import './Home.css';
import { ArrowDownIcon } from '@heroicons/react/24/solid';
import OptionsMenu from '../components/OptionsMenu';
import type { Photo } from '../types/photo';
import type { ApiArtist, Artist } from '../types/master-data';
import type { PageProps } from '../types/global';
import {
  artistPortraitPath,
  artistPortraitUrl,
  getAllPhotos,
  getArtists
} from '../utils/photos-db';

interface ShuffleArray {
  <T>(array: Array<T>): Array<T>;
}

const ARTIST_PLACEHOLDER = '/photographers/placeholder.png';

/** The colour, visual and photographer steps are one "vibe check" grid now, so
 *  this single gate stands in for the three separate five-photo ones. */
const MINIMUM_SELECTION = 10;

/** Below this, the fully-tagged pool is too thin to fill a grid and the vibe
 *  check widens to anything carrying at least one of the three taxonomies. */
const FULLY_TAGGED_FLOOR = 30;

const hasColor = (photo: Photo) => (photo.catalog_colors?.length ?? 0) > 0;
const hasStyle = (photo: Photo) => (photo.catalog_styles?.length ?? 0) > 0;
const hasArtist = (photo: Photo) => (photo.artists?.length ?? 0) > 0;

/**
 * One step has to fill all three summary rows on its own, so prefer photos
 * tagged with a colour AND a style AND a photographer: every pick then feeds
 * every row, the way the three separate steps used to guarantee between them.
 * A sparsely tagged catalogue falls back to the union rather than show a bare
 * grid - the summary can then come up short on a row, as it could before.
 */
const getVibeCheckPhotos = (photos: Photo[]) => {
  const fullyTagged = photos.filter(photo => hasColor(photo) && hasStyle(photo) && hasArtist(photo));
  if (fullyTagged.length >= FULLY_TAGGED_FLOOR) return fullyTagged;

  return photos.filter(photo => hasColor(photo) || hasStyle(photo) || hasArtist(photo));
};

const shuffleArray: ShuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

function Page(props: PageProps) {
  const { 
    page, 
    onNextPage, 
    onSessionSelect, 
    onPhotoSelect, 
    selectedPhotos,
    selectedSession,
    onReset 
  } = props;
  const [photos, setPhotos] = useState<Photo[]>([]);
  const cachedPhotosRef = useRef<Photo[]>([]);
  // The roster carries `photo_closeup`, which the artist copies nested in each
  // photo deliberately omit - so the summary has to look it up by `artist_id`.
  const [roster, setRoster] = useState<Artist[]>([]);
  // Object URLs for the portraits cached by the sync, keyed by artist_id.
  const [portraitUrls, setPortraitUrls] = useState<Map<number, string>>(new Map());

  const loadPhotos = async () => {
    if (page.type !== 'selection-page' && page.type !== 'summary-page') return;

    // Only fetch photos if not cached
    if (cachedPhotosRef.current.length === 0) {
      const photoData = await getAllPhotos();
      // Shuffle photos right after fetching
      cachedPhotosRef.current = shuffleArray([...photoData]);
      console.log('Fetching and shuffling photos from DB');
    }

    const sessionPhotos = cachedPhotosRef.current.filter(photo =>
      photo.catalog_sessions.some(session =>
        session.name.toLowerCase() === selectedSession?.toLowerCase()
      )
    );

    // The summary only ever renders what was picked, so don't mint object URLs
    // for the rest of the catalogue.
    const visible = page.type === 'summary-page'
      ? sessionPhotos.filter(photo => selectedPhotos.has(photo.id))
      : getVibeCheckPhotos(sessionPhotos);

    // If no photos available, automatically go to next page
    if (page.type === 'selection-page' && visible.length === 0) {
      onNextPage();
      return;
    }

    setPhotos(visible.map(photo => ({
      ...photo,
      url: photo.blob ? URL.createObjectURL(photo.blob) : undefined
    })));
  };

  // `selectedPhotos` is read on mount for the summary and deliberately left out
  // of the deps: Page remounts per wizard step, so the value is current, and
  // re-running per tap would rebuild every object URL in the grid.
  useEffect(() => {
    loadPhotos();
  }, [page.id, selectedSession]);

  // Keyed on `photos` so each batch of object URLs is revoked when it is
  // replaced, rather than whatever the effect closed over on its first run.
  useEffect(() => {
    return () => {
      photos.forEach(photo => photo.url && URL.revokeObjectURL(photo.url));
    };
  }, [photos]);

  useEffect(() => {
    if (page.type !== 'summary-page') return;

    let cancelled = false;

    getArtists()
      .then(artists => {
        if (!cancelled) setRoster(artists);
      })
      .catch(error => console.error('Failed to load artist roster:', error));

    return () => {
      cancelled = true;
    };
  }, [page.type]);

  useEffect(() => {
    const urls = new Map<number, string>();

    roster.forEach(artist => {
      if (artist.portrait) {
        urls.set(artist.artist_id, URL.createObjectURL(artist.portrait));
      }
    });

    setPortraitUrls(urls);

    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [roster]);

  // Update the masonry grid to show category information
  const renderPhotoItem = (photo: Photo) => {
    const isSummary = page.type === 'summary-page';
    const isSelected = selectedPhotos.has(photo.id);

    return (
      <div 
        key={photo.id} 
        className={`masonry-item ${isSelected && !isSummary ? 'selected' : ''}`}
        onClick={() => !isSummary && onPhotoSelect(photo.id, !isSelected)}
        style={{ cursor: isSummary ? 'default' : 'pointer' }}
      >
        <img 
          src={photo.url}
          alt={`Photo ${photo.id}`}
          className="masonry-image"
        />
        {!isSummary && isSelected && (
          <div className="photo-overlay">
            <div className="select-indicator">
              <img 
                src="/assets/dandelion.png"
                alt="Selected"
                className="check-icon"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSummaryPage = (selectedPhotos: Photo[]) => {
    return (
        <div>
            <div className="summary-page">
                <motion.div 
                    className="summary-header"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                >
                    <img 
                      src="assets/summary_header.png"
                      width="50%"
                    />
                </motion.div>

                <motion.div 
                    className="summary-content"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                >
                    {renderSummaryCard(selectedPhotos)}
                </motion.div>

                <motion.div 
                    className="summary-footer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                >
                <img 
                    src="assets/summary_contact_us.png"
                    width={100}
                />
                </motion.div>

                <motion.div 
                    className="summary-footer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                >
                <img 
                    src="assets/summary_snap.png"
                    width={180}
                />
                </motion.div>

                 <Masonry
                breakpointCols={{
                  default: 5,
                  1100: 5,
                  700: 4,
                  500: 3
                }}
                className="masonry-grid"
                columnClassName="masonry-grid-column"
              >
                {selectedPhotos.map(renderPhotoItem)}
              </Masonry>
       
            </div>
            <motion.button
                className="reset-button"
                onClick={onReset}
                >
                Start Over
            </motion.button>
        </div>
    );
  };

  // Update error state to handle auto-dismiss
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const errorTimeoutRef = useRef<number>(0);

  const showError = (message: string) => {
    setSelectionError(message);
    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      window.clearTimeout(errorTimeoutRef.current);
    }
    // Set new timeout to clear error after 5 seconds
    errorTimeoutRef.current = window.setTimeout(() => {
      setSelectionError(null);
    }, 5000);
  };

  const selectedCount = photos.filter(photo => selectedPhotos.has(photo.id)).length;

  // Update check function to use new error handling
  const checkMinimumSelection = () => {
    if (page.type === 'selection-page' && selectedCount < MINIMUM_SELECTION) {
      showError(`Please select at least ${MINIMUM_SELECTION} photos`);
      return false;
    }
    return true;
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        window.clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const renderSummaryCard = (photos: Photo[]) => {
    // Group photos by their categories
    const categories = {
      colors: new Map<string, Photo[]>(),
      vibes: new Map<string, Photo[]>(),
      photographers: new Map<string, Photo[]>()
    };

    // Artist portraits come from the roster rather than a filename guessed from
    // the name: the old `catalog_artists` names happened to match files in
    // public/photographers, but roster names are full names ("Ken Sai").
    const artistsByName = new Map<string, ApiArtist>();
    const rosterById = new Map(roster.map(artist => [artist.artist_id, artist]));

    // Group photos by categories
    photos.forEach(photo => {
      photo.catalog_colors?.forEach(color => {
        if (!categories.colors.has(color.name)) {
          categories.colors.set(color.name, []);
        }
        categories.colors.get(color.name)?.push(photo);
      });
      photo.catalog_styles?.forEach(style => {
        if (!categories.vibes.has(style.name)) {
          categories.vibes.set(style.name, []);
        }
        categories.vibes.get(style.name)?.push(photo);
      });
      photo.artists?.forEach(artist => {
        if (!categories.photographers.has(artist.name)) {
          categories.photographers.set(artist.name, []);
        }
        categories.photographers.get(artist.name)?.push(photo);
        artistsByName.set(artist.name, artist);
      });
    });

    // The sync caches each portrait as a blob, so prefer that and stay offline.
    // Fall back to fetching it over /s3, then to the bundled placeholder.
    const artistPhotoUrl = (name: string) => {
      const nested = artistsByName.get(name);
      const rosterArtist = nested ? rosterById.get(nested.artist_id) : undefined;

      const cached = nested && portraitUrls.get(nested.artist_id);
      if (cached) return cached;

      const path = artistPortraitPath(rosterArtist ?? nested);
      return path ? artistPortraitUrl(path) : ARTIST_PLACEHOLDER;
    };

    const rows = [
      { 
        type: 'colors',
        title: 'your <strong>color</strong> vibes', 
        items: Array.from(categories.colors.entries())
          .sort(([, a], [, b]) => b.length - a.length)
          .slice(0, 3)  // Limit to first 3 items
          .map(([name, photos]) => ({ name, photo: photos[0], count: photos.length }))
      },
      { 
        type: 'vibes',
        title: 'your  <strong>visual</strong> vibes', 
        items: Array.from(categories.vibes.entries())
          .sort(([, a], [, b]) => b.length - a.length)
          .slice(0, 3)  // Limit to first 3 items
          .map(([name, photos]) => ({ name, photo: photos[0], count: photos.length }))
      },
      { 
        type: 'photographers',
        title: 'your  <strong>photographers</strong>', 
        items: Array.from(categories.photographers.entries())
          .sort(([, a], [, b]) => b.length - a.length)
          .slice(0, 3)  // Limit to first 3 items
          .map(([name, photos]) => ({ name, photo: photos[0], count: photos.length }))
      }
    ];

    return (
      <div className="summary-card">
        {rows.map((row, rowIndex) => (
         <div key={rowIndex} className="summary-row">
            <h3 className="summary-row-title" dangerouslySetInnerHTML={{ __html: row.title }} />
            { row.type !== 'photographers' && (
                <div className="summary-photo-grid">
                    {row.items.map(({ name, photo, count }) => (
                        <div className="summary-photo-container">
                            <div key={name} className="summary-photo-item">
                            <img 
                                src={photo.url} 
                                alt={`${name}`}
                                className="summary-photo"
                            />
                            </div>
                            <div className="photo-tags">
                                <span className="category-tag">
                                {name}
                                <span className="tag-count">{count}</span>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            { row.type === 'photographers' && (
                <div className="summary-photo-grid">
                    {row.items.map(({ name, count }) => (
                        <div className="summary-photographers-container">
                            <div key={name} className="summary-photo-item">
                            <img 
                                src={artistPhotoUrl(name)} 
                                alt={`${name}`}
                                className="summary-photo"
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  if (!img.src.endsWith(ARTIST_PLACEHOLDER)) {
                                    img.src = ARTIST_PLACEHOLDER;
                                  }
                                }}
                            />
                            </div>
                            <div className="photo-tags">
                                <span className="category-tag">
                                {name}
                                <span className="tag-count">{count}</span>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        ))}
      </div>
    );
  };

  return (
    <section className="page-container">
      {page.header ? (
        <motion.h2
          className="floating-title"
          style={{
            backgroundColor: page.header.backgroundColor || page.header.color,
          }}
        >
            <h2 className='title' style={{ color: page.header.color }}>{page.header.title}</h2>
            {page.header.description ? (
                <span className="title-description" style={{ color: page.header.color }}>{page.header.description}</span>
            ) : null}
        </motion.h2>
      ) : null}

      <div className={page.type == "general-page" ? "full-screen-wrapper" : "content-wrapper"}>
        {page.type === "summary-page" ? (
          renderSummaryPage(photos)
        ) : page.type === "general-page" ? (
          <div className="full-screen-container" style={{ 
            backgroundColor: page.backgroundColor ? page.backgroundColor : 'transparent'
          }}>
            {page.fullScreenImage && (
              <motion.img
                src={page.fullScreenImage}
                alt={page.header?.title}
                className="full-screen-image"
                initial={{ scale: 1, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
              />
            )}
            {page.centeredImage && (
              <motion.div 
                className="centered-image-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                style={{ width: page.centeredImage.width, marginLeft: page.centeredImage.marginLeft ? page.centeredImage.marginLeft : '0' }}
              >
                <img 
                  src={page.centeredImage.src}
                  alt="Centered Image" 
                  className="centered-image"
                />
                {page.centeredImage.buttons && (
                  <div className="centered-buttons">
                    <button 
                      className="centered-button btn-primary"
                      onClick={() => onSessionSelect('prewedding')}
                    >
                      {page.centeredImage.buttons[0]}
                    </button>
                    <button 
                      className="centered-button btn-primary"
                      onClick={() => onSessionSelect('wedding')}
                    >
                      {page.centeredImage.buttons[1]}
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        ) : (
          // ...existing selection-page rendering...
          <div className="content-area">
            {page.type === 'summary-page' ? (
              renderSummaryPage(photos)
            ) : (
              <Masonry
                breakpointCols={{
                  default: 4,
                  1100: 4,
                  700: 3,
                  500: 2
                }}
                className="masonry-grid"
                columnClassName="masonry-grid-column"
              >
                {photos.map(renderPhotoItem)}
              </Masonry>
            )}
          </div>
        )}
        
        {!page.hideNextButton && page.type !== 'summary-page' && (
          <>
          <AnimatePresence>
            {selectionError && (
              <motion.div 
                className="selection-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {selectionError}
              </motion.div>
            )}
          </AnimatePresence>
            <button 
              className="next-page-button" 
              onClick={() => {
                if (checkMinimumSelection()) {
                  onNextPage();
                }
              }}
              aria-label="Next page"
            >
              {page.type === 'selection-page' && (
                <span className={`selection-count ${selectedCount >= MINIMUM_SELECTION ? 'complete' : ''}`}>
                  {selectedCount}/{MINIMUM_SELECTION}
                </span>
              )}
              <ArrowDownIcon width={48} height={48} />
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * Wizard steps. The ids are historical - the colour, visual and photographer
 * steps that were 5, 6 and 7 are now the single "vibe check" grid - so nothing
 * keys off them any more; the flow is driven by `type` and array order.
 */
const PAGES = [
  { 
    id: 1, 
    type: 'general-page', 
    fullScreenImage: '/assets/cover_2.jpg', 
    centeredImage: {
      src: '/assets/assets_3.png',
      width: '50%',
      marginLeft: '-20%',
      buttons: [
        'pre-wedding',
        'wedding'
      ]
    },
    hideNextButton: true
  },
  { 
    id: 2, 
    type: 'general-page', 
    backgroundColor: '#f12536', 
    centeredImage: {
      src: '/assets/assets_2.png',
      width: '90%'
    },
    hideNextButton: false
  },
  { 
    id: 5, 
    type: 'selection-page', 
    header: {
      title: 'vibe check',
      description: `\u{1F4F8} pick ${MINIMUM_SELECTION}+ photos that pass the vibe check`,
      color: '#f12536',
      backgroundColor: '#ffbc3fff'
    }
  },
  { 
    id: 8, 
    type: 'summary-page'
  }
];

export default function Home() {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedSession, setSelectedSession] = useState<string | undefined>(undefined);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<number>>(new Set());

  const handleReset = () => {
    setSelectedPhotos(new Set());
    setCurrentPage(0);
    setSelectedSession(undefined);
  };

  const handleSessionSelect = (sessionType: string) => {
    setSelectedSession(sessionType);
    setCurrentPage(prev => prev + 1);
  };

  const handlePhotoSelect = async (photoId: number, isSelected: boolean) => {
    try {
        setSelectedPhotos(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(photoId);
            } else {
                newSet.delete(photoId);
            }
            return newSet;
        });
    } catch (error) {
        console.error('Failed to toggle photo selection:', error);
    }
  };

  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => (prev < PAGES.length - 1 ? prev + 1 : prev));
  }, []);

  return (
    <div className="wizard-wrapper">
      <OptionsMenu />
      <AnimatePresence initial={false} mode="popLayout">
        <motion.div
          key={currentPage}
          className="wizard-page"
          initial={{ position: 'absolute', y: '100%' }}
          animate={{ position: 'absolute', y: '0' }}
          exit={{ position: 'absolute', y: '-100%' }}
          transition={{ duration: 1, ease: "easeInOut" }}
        >
          <Page 
            page={PAGES[currentPage]}
            onNextPage={handleNextPage}
            onSessionSelect={handleSessionSelect}
            onPhotoSelect={handlePhotoSelect}
            selectedPhotos={selectedPhotos}
            selectedSession={selectedSession}
            onReset={handleReset}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}